import { generateFeedSlides } from 'common/utils/ppt/feedSlideGenerators';
import { generateProfileCardSlides } from 'common/utils/ppt/profileCardSlideGenerators';
import { useGlobalKolPlanningContext } from 'context/globalKolPlanningContext';
import { useSocialMonitoring } from 'context/socialMonitoringContext';
import { useUser } from 'context/userContext';
import PPTxGen from 'pptxgenjs';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  COLOR_COVER_SUBTITLE,
  COLOR_HEADER_PROJECT,
  COLOR_HEADER_SEPARATOR,
  COLOR_HEADER_TITLE,
  COLOR_SLIDE_BG,
  FONT_SIZE_H2,
  FOOTER_DISCLAIMER,
  FOOTER_TEXT,
  FOOTER_TEXT_STYLE,
  MARGIN_LEFT,
  PPT_LAYOUT,
} from './ppt-constants';

export function usePptExport(id) {
  const { projects } = useSocialMonitoring();
  const { projectDisplayName } = useGlobalKolPlanningContext();
  const { projectId } = useParams();
  const { getProjectDisplayName } = useUser();

  const project = projects?.find(p => p.projectID === projectId) ?? {};
  const projectName = projectDisplayName || getProjectDisplayName(project);

  const [isPPTExportLoading, setPPTExportLoading] = useState(false);

  const pptFormatDate = useCallback(date => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', options);
    return formattedDate;
  }, []);

  const addSlideHeader = useCallback(
    (slide, customTitle) => {
      // Header background is now part of the master slide
      // Add all header text as a single element to avoid overlap
      const headerText = [
        {
          text: projectName,
          options: {
            color: COLOR_HEADER_PROJECT, // White for project name
            bold: false,
          },
        },
        {
          text: ' | ',
          options: {
            color: COLOR_HEADER_SEPARATOR, // Blue-gray for separator
            bold: false,
          },
        },
        {
          text: customTitle,
          options: {
            color: COLOR_HEADER_TITLE, // Light blue for title
            bold: false,
          },
        },
      ];

      slide.addText(headerText, {
        x: 0.2,
        y: 0,
        fontSize: FONT_SIZE_H2,
        h: 0.8,
        valign: 'middle',
      });
    },
    [projectName],
  );

  const addSlideFooter = useCallback(slide => {
    slide.addText(FOOTER_DISCLAIMER, { ...FOOTER_TEXT_STYLE, bold: true, x: 0.1 });

    slide.addText(FOOTER_TEXT, FOOTER_TEXT_STYLE);
  }, []);

  const exportToPpt = useCallback(
    (title, genSlides) => {
      const pptx = new PPTxGen();
      pptx.layout = PPT_LAYOUT; // Set slide layout
      // Master Slide
      pptx.defineSlideMaster({
        title: 'COVER_SLIDE',
        background: { path: '/ppt-export/pptx-cover.png' },
      });

      // Content slide master with header background included
      pptx.defineSlideMaster({
        title: 'CONTENT_SLIDE',
        background: { color: COLOR_SLIDE_BG }, // NextGen Light Blue/Grey background
        margin: [MARGIN_LEFT, 0.25, 1.0, 0.25],
        objects: [
          // Header background as part of master slide
          {
            image: {
              path: '/ppt-export/pptx-header.svg',
              x: 0,
              y: 0,
              w: '100%',
              h: 0.8, // Adjusted height to preserve SVG aspect ratio
            },
          },
        ],
      });
      // Cover slide
      const coverSlide = pptx.addSlide({ masterName: 'COVER_SLIDE' });
      coverSlide.addText(projectName, {
        x: 0.5,
        y: '30%',
        w: '90%',
        h: 0.7,
        fontSize: 40,
        color: 'FFFFFF',
        bold: true,
        align: 'left',
      });
      coverSlide.addText(title || 'Project Summary', {
        x: 0.5,
        y: '45%',
        h: 0.55,
        fontSize: 24,
        color: COLOR_COVER_SUBTITLE,
        align: 'left',
      });

      const today = new Date();
      const formattedDate = pptFormatDate(today);

      coverSlide.addText(formattedDate, {
        x: 0.5,
        y: '55%',
        fontSize: 16,
        fontFace: 'Source Sans Pro',
        color: COLOR_COVER_SUBTITLE,
        align: 'left',
      });

      // Logo
      coverSlide.addImage({
        path: '/ppt-export/pptx-logo.svg',
        x: '75%',
        y: '80%',
        w: 1.76,
        h: 0.55,
      });

      genSlides(pptx);
      return pptx.writeFile({ fileName: `${projectName} - ${title}.pptx` });
    },
    [projectName, pptFormatDate],
  );

  // New enhanced export function supporting NextGen-style slides array
  const exportToPptWithSlides = useCallback(
    options => {
      /* eslint-disable no-unused-vars */
      const {
        title,
        subtitle,
        coverTitle, // Override for cover slide main title (e.g., "Project Summary")
        coverSubtitle, // Override for cover slide subtitle (e.g., project name)
        includeCoverSlide = true,
        slides,
        fileName,
        feedData,
        feedAvatarMap,
        linkIconBase64,
        feedFilters,
        // Support for multiple feed types (Project Export)
        ownedFeedData,
        ownedFeedAvatarMap,
        ownedFeedLinkIcon,
        ownedFeedDefaultAvatar,
        earnedFeedData,
        earnedFeedAvatarMap,
        earnedFeedLinkIcon,
        earnedFeedDefaultAvatar,
        // Support for profile card slides (Project Export)
        reachProfileCardData,
        reachProfileCardAvatarMap,
        reachProfileCardDefaultAvatar,
        ownedProfileCardData,
        ownedProfileCardAvatarMap,
        ownedProfileCardDefaultAvatar,
        earnedProfileCardData,
        earnedProfileCardAvatarMap,
        earnedProfileCardDefaultAvatar,
        // Social icons map (pre-fetched for efficiency)
        socialIconsMap,
        hideSlideFooter = false,
      } = options;
      /* eslint-enable no-unused-vars */
      const pptx = new PPTxGen();
      pptx.layout = PPT_LAYOUT;
      const addConfiguredSlideFooter = hideSlideFooter ? undefined : addSlideFooter;

      // Define master slides
      pptx.defineSlideMaster({
        title: 'COVER_SLIDE',
        background: { path: '/ppt-export/pptx-cover.png' },
      });

      // Content slide master with header background included
      pptx.defineSlideMaster({
        title: 'CONTENT_SLIDE',
        background: { color: COLOR_SLIDE_BG }, // NextGen Light Blue/Grey background
        margin: [MARGIN_LEFT, 0.25, 1.0, 0.25],
        objects: [
          // Header background as part of master slide
          {
            image: {
              path: '/ppt-export/pptx-header.svg',
              x: 0,
              y: 0,
              w: '100%',
              h: 0.8, // Adjusted height to preserve SVG aspect ratio
            },
          },
        ],
      });

      // Profile slide master with header and white content area
      pptx.defineSlideMaster({
        title: 'PROFILE_SLIDE',
        background: { color: COLOR_SLIDE_BG }, // NextGen Light Blue/Grey background
        margin: [MARGIN_LEFT, 0.25, 1.0, 0.25],
        objects: [
          // Header background as part of master slide
          {
            image: {
              path: '/ppt-export/pptx-header.svg',
              x: 0,
              y: 0,
              w: '100%',
              h: 0.8,
            },
          },
          // White content area for charts and profile info - full width and starting higher
          {
            rect: {
              x: 0,
              y: 2.8, // Start higher (was 3.1)
              w: '100%', // Full width
              h: 4.2, // Extend to bottom (was 3.6)
              fill: { color: 'FFFFFF' },
              line: { color: 'FFFFFF', width: 0 }, // No border
              rectRadius: 0,
            },
          },
        ],
      });

      if (includeCoverSlide) {
        // Create cover slide - NextGen style
        const coverSlide = pptx.addSlide({ masterName: 'COVER_SLIDE' });

        // Main title - Use coverTitle override if provided, otherwise projectName or title
        // For project exports: coverTitle = "Project Summary"
        // For profile exports: uses projectName || title
        const mainTitleText = coverTitle || projectName || title || 'Export';
        coverSlide.addText(mainTitleText, {
          x: 0.5,
          y: '30%',
          w: '90%',
          h: 0.7,
          fontSize: 40,
          fontFace: 'Source Sans Pro',
          color: 'FFFFFF',
          bold: true,
          align: 'left',
          fit: 'shrink',
        });

        // Subtitle - Use coverSubtitle override if provided, otherwise derive from other options
        // For project exports: coverSubtitle = projectName
        // For profile exports: uses title as subtitle when projectName is available
        const subtitleText = coverSubtitle || (projectName ? title || subtitle : subtitle);
        if (subtitleText) {
          coverSlide.addText(subtitleText, {
            x: 0.5,
            y: '45%',
            h: 0.55,
            fontSize: 24,
            fontFace: 'Source Sans Pro',
            color: COLOR_COVER_SUBTITLE,
            align: 'left',
            fit: 'shrink',
          });
        }

        // Date
        const today = new Date();
        const formattedDate = pptFormatDate(today);

        coverSlide.addText(formattedDate, {
          x: 0.5,
          y: '55%',
          fontSize: 16,
          fontFace: 'Source Sans Pro',
          color: COLOR_COVER_SUBTITLE,
          align: 'left',
        });

        // Logo
        coverSlide.addImage({
          path: '/ppt-export/pptx-logo.svg',
          x: '75%',
          y: '80%',
          w: 1.76,
          h: 0.55,
        });
      }

      // Generate content slides from slides array
      if (slides && Array.isArray(slides)) {
        const uniqueSectionTitles = slides.reduce((sectionTitles, slideContent) => {
          const sectionTitle = String(slideContent?.sectionTitle || '').trim();

          if (!sectionTitle || sectionTitles.includes(sectionTitle)) {
            return sectionTitles;
          }

          return [...sectionTitles, sectionTitle];
        }, []);
        const supportsSections = typeof pptx.addSection === 'function';

        if (supportsSections) {
          uniqueSectionTitles.forEach(sectionTitle => {
            pptx.addSection({ title: sectionTitle });
          });
        }

        slides.forEach(slideContent => {
          // Use PROFILE_SLIDE master for profile overview slides
          const masterName = slideContent.isProfileOverview ? 'PROFILE_SLIDE' : 'CONTENT_SLIDE';
          const slideOptions = { masterName };
          const sectionTitle = String(slideContent?.sectionTitle || '').trim();

          if (sectionTitle && supportsSections) {
            slideOptions.sectionTitle = sectionTitle;
          }

          const slide = pptx.addSlide(slideOptions);
          addSlideHeader(slide, slideContent.title || '');

          // Call the content function with slide and pptx
          if (typeof slideContent.content === 'function') {
            slideContent.content(slide, pptx);
          }

          addConfiguredSlideFooter?.(slide);
        });
      }

      // Handle profile card slides - Top Profiles by Reach (Project Export)
      // These come BEFORE feed slides in the export order
      if (reachProfileCardData && reachProfileCardData.length > 0) {
        generateProfileCardSlides(
          pptx,
          reachProfileCardData,
          reachProfileCardAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          'Top Profiles | Reach',
          reachProfileCardDefaultAvatar,
          socialIconsMap,
        );
      }

      // Handle profile card slides - Top Profiles by Owned (Project Export)
      if (ownedProfileCardData && ownedProfileCardData.length > 0) {
        generateProfileCardSlides(
          pptx,
          ownedProfileCardData,
          ownedProfileCardAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          'Top Profiles | Owned',
          ownedProfileCardDefaultAvatar,
          socialIconsMap,
        );
      }

      // Handle profile card slides - Top Profiles by Earned (Project Export)
      if (earnedProfileCardData && earnedProfileCardData.length > 0) {
        generateProfileCardSlides(
          pptx,
          earnedProfileCardData,
          earnedProfileCardAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          'Top Profiles | Earned',
          earnedProfileCardDefaultAvatar,
          socialIconsMap,
        );
      }

      // Handle feed data separately if provided (creates multiple slides)
      if (feedData && feedAvatarMap) {
        // Extract default avatar from the avatarMap if it exists
        const defaultAvatarBase64 = feedAvatarMap.get('defaultAvatar') || null;
        generateFeedSlides(
          pptx,
          feedData,
          feedAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          pptFormatDate,
          linkIconBase64,
          defaultAvatarBase64,
          feedFilters,
        );
      }

      // Handle owned feed data (Project Export)
      if (ownedFeedData && ownedFeedAvatarMap) {
        generateFeedSlides(
          pptx,
          ownedFeedData,
          ownedFeedAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          pptFormatDate,
          ownedFeedLinkIcon,
          ownedFeedDefaultAvatar, // Use emptyProfilePlaceholder for missing avatars
          feedFilters,
        );
      }

      // Handle earned feed data (Project Export)
      if (earnedFeedData && earnedFeedAvatarMap) {
        generateFeedSlides(
          pptx,
          earnedFeedData,
          earnedFeedAvatarMap,
          addSlideHeader,
          addConfiguredSlideFooter,
          pptFormatDate,
          earnedFeedLinkIcon,
          earnedFeedDefaultAvatar, // Use emptyProfilePlaceholder for missing avatars
          feedFilters,
        );
      }

      return pptx.writeFile({ fileName: fileName || `${projectName} - ${title}.pptx` });
    },
    [addSlideFooter, addSlideHeader, pptFormatDate, projectName],
  );

  return useMemo(
    () => ({
      id,
      exportToPpt,
      exportToPptWithSlides,
      addSlideFooter,
      pptFormatDate,
      addSlideHeader,
      setPPTExportLoading,
      isPPTExportLoading,
    }),
    [
      id,
      exportToPpt,
      exportToPptWithSlides,
      addSlideFooter,
      pptFormatDate,
      addSlideHeader,
      setPPTExportLoading,
      isPPTExportLoading,
    ],
  );
}
